import { useApolloClient } from '@apollo/client';

import type {
  AiOriginalImageFragment,
  AiOriginalImageQuery,
  AiOriginalImageQueryVariables,
} from 'src/shared/graphql/generated';
import {
  AiOriginalImageDocument,
  useAiOriginalImageQuery,
} from 'src/shared/graphql/generated';
import { TranslationNamespace, useTranslate } from 'src/shared/hooks';

import type { AiOrSampleDisplayImage } from '../../types';
import {
  getImageId,
  isAiDisplay,
  isAiGenerated,
  isAiOriginal,
  isMyUpload,
  isSample,
} from '../../types';

import type { Tracking } from './types';

export interface ImageDisplay {
  url: string;
  alt: string;
}

export interface ImagePair {
  primaryImage: ImageDisplay;
  toggleImage: ImageDisplay | null;
  tracking: Tracking;
  loading?: boolean;
}

/**
 * Given a generated image or original image, return the appropriate image
 * pairing for swapping between original and generated along with the
 * appropriate tracking metadata. This handles both user and sample images.
 *
 * 1. Given a user-generated image, fetch the original as its pair.
 * 2. Given a sample generated image, retrieve the associated sample original.
 * 3. Given an original image, don't return a pairing but include the proper
 * tracking metadata.
 *
 * @param image generated or original image (or sample of either)
 * @param skipQuery signals skip to useAiOriginalImageQuery
 *
 * @returns image pairing with image URLs and localized alt text
 */
export const useImagePair = (
  image: AiOrSampleDisplayImage & {
    aiOriginalImage?: AiOriginalImageFragment | null;
  },
  skipQuery: boolean = false,
): ImagePair => {
  const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);
  const originalImage = image?.aiOriginalImage;
  const isSampleImage = isSample(image);
  const hasOriginalPairing =
    (!isSampleImage && !isMyUpload(image)) || !!originalImage?.image;

  const skip = skipQuery || !hasOriginalPairing;

  const client = useApolloClient();

  const id = isSampleImage ? '' : String(image.aiOriginalImageId);

  // Check in cache for `AiOriginalImage` query results. If we have a cache hit,
  // then we ignore the skip parameter to allow an immediate value for original
  // image data to be returned
  const cachedAiOriginalImage =
    originalImage ??
    client.readQuery<AiOriginalImageQuery, AiOriginalImageQueryVariables>({
      query: AiOriginalImageDocument,
      variables: { id },
    });

  const { data, loading } = useAiOriginalImageQuery({
    variables: { id },
    skip: !!originalImage?.image || (!cachedAiOriginalImage && skip),
  });
  const { spaceType, style } = image;

  const generated = image.aiGeneratedImage;
  const original = isSampleImage
    ? image.getSampleOriginalImage()
    : originalImage ?? data?.aiOriginalImage;
  const originalImagePath =
    original?.image ?? data?.aiOriginalImage?.image ?? '';

  if (isMyUpload(image)) {
    return {
      primaryImage: {
        url: generated.generatedImagePath,
        alt: t('fullscreenImage.originalAlt', { spaceType: image.spaceType }),
      },
      toggleImage: null,
      tracking: {
        ai_display_image_id: isAiDisplay(image) ? image.id : undefined,
        output_id: String(getImageId(image)),
      },
    };
  }

  return {
    loading,
    primaryImage: {
      url: generated.generatedImagePath,
      alt: t('fullscreenImage.generatedAlt', { spaceType, style }),
    },
    toggleImage: original
      ? {
          url: originalImagePath,
          alt: t('fullscreenImage.originalAlt', { spaceType }),
        }
      : null,
    tracking: {
      ai_display_image_id: isAiDisplay(image) ? image.id : undefined,
      ai_original_image_id: isAiOriginal(original) ? original.id : undefined,
      ai_generated_image_id: isAiGenerated(generated)
        ? generated.id
        : undefined,
      // TODO(MEOW-3290): figure out what we want to track when we're
      // displaying a sample display image
      output_id: String(getImageId(image)),
    },
  };
};
